<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type { HeidiMessage, MHeidiRecorderProps } from "./MHeidiRecorder.props";
import { mpfHeidiRecorderDefaults } from "./MHeidiRecorder.props";

const props = withDefaults(defineProps<MHeidiRecorderProps>(), mpfHeidiRecorderDefaults);

const emit = defineEmits<{
  (e: "ready"): void;
  (e: "session-started", value?: string): void;
  (e: "open", value?: string): void;
  (e: "resize", expanded: boolean): void;
  (e: "push-data", value?: string): void;
  (e: "close"): void;
  (e: "load"): void;
}>();

const iframeRef = ref<HTMLIFrameElement | null>(null);
const isLoaded = ref(false);
const isVisible = ref(false);
const isExpanded = ref(false);

const iframeHeight = computed(() => {
  return `${isExpanded.value ? props.expandedHeight : props.collapsedHeight}px`;
});

const iframeStyle = computed(() => ({
  height: iframeHeight.value,
}));

function handleIframeMessage(event: MessageEvent<HeidiMessage>) {
  if (event.origin !== window.location.origin) return;
  switch (event.data?.type) {
    case "HEIDI_READY":
      isVisible.value = true;
      emit("ready");
      break;

    case "HEIDI_SESSION_STARTED":
      emit("session-started", event.data.payload?.data);
      break;

    case "HEIDI_OPEN":
      isVisible.value = true;
      emit("open", event.data.payload?.data);
      break;

    case "HEIDI_RESIZE": {
      isExpanded.value = !!event.data.payload;
      emit("resize", isExpanded.value);
      break;
    }

    case "HEIDI_PUSH_DATA":
      emit("push-data", event.data.payload?.data);
      break;

    case "HEIDI_CLOSE":
      isVisible.value = false;
      isExpanded.value = false;
      emit("close");
      break;
  }
}

function handleIframeLoad() {
  isLoaded.value = true;
  isVisible.value = true;
  emit("load");
}

onMounted(() => {
  window.addEventListener("message", handleIframeMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener("message", handleIframeMessage);
});

watch(
  () => props.src,
  (newSrc) => {
    if (!newSrc) {
      isLoaded.value = false;
      isVisible.value = false;
      isExpanded.value = false;
    }
  },
);
</script>

<template>
  <div :class="{ loaded: isLoaded }">
    <iframe
      v-show="isVisible && src"
      :id="iframeId"
      ref="iframeRef"
      class="medicus-heidi-iframe"
      :src="src"
      :style="iframeStyle"
      @load="handleIframeLoad"
    />
  </div>
</template>

<style scoped lang="scss">
.medicus-heidi-iframe {
  width: calc(100% + 16px);
  height: 220px;

  // Counter the Heidi padding
  margin-left: -8px;
  margin-right: -8px;
  overflow: hidden;

  border: none;
}
</style>
